'use client';

import * as CheckboxPrimitive from '@radix-ui/react-checkbox';
import { Check, Minus } from 'phosphor-icons';
import { forwardRef } from 'react';
import { c } from '../../../utils/tailwind-merge';
import type { CheckboxV2Props } from './Checkbox.v2.types';

const CHECKED_STYLES =
  'data-[state=checked]:border-blue-hover data-[state=indeterminate]:border-blue-hover data-[state=checked]:bg-blue data-[state=indeterminate]:bg-blue';

function getCheckboxStateStyles(disabled: boolean, isDark: boolean): string {
  if (disabled) {
    return isDark
      ? 'cursor-not-allowed border-grey-600 bg-grey-800'
      : 'cursor-not-allowed border-grey-400 bg-grey-200';
  }
  return isDark
    ? `cursor-pointer border-grey-400 bg-transparent ${CHECKED_STYLES}`
    : `cursor-pointer border-grey-500 bg-white hover:border-grey-700 hover:bg-grey-200 ${CHECKED_STYLES}`;
}

export const CheckboxV2 = forwardRef<HTMLButtonElement, CheckboxV2Props>(
  function CheckboxV2(
    {
      checked,
      defaultChecked,
      disabled = false,
      required,
      name,
      value,
      id,
      onCheckedChange,
      className,
      isDarkTheme = false,
    },
    ref
  ) {
    return (
      <CheckboxPrimitive.Root
        ref={ref}
        id={id}
        checked={checked}
        defaultChecked={defaultChecked}
        disabled={disabled}
        required={required}
        name={name}
        value={value}
        onCheckedChange={(checkedState) => {
          onCheckedChange?.(checkedState === true);
        }}
        className={c(
          'flex size-4 shrink-0 items-center justify-center rounded',
          'border transition-colors',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue focus-visible:ring-offset-2',
          getCheckboxStateStyles(disabled, isDarkTheme),
          className
        )}
      >
        <CheckboxPrimitive.Indicator className="flex items-center justify-center text-white">
          {checked === 'indeterminate' ? (
            <Minus size={12} weight="bold" aria-hidden="true" />
          ) : (
            <Check size={12} weight="bold" aria-hidden="true" />
          )}
        </CheckboxPrimitive.Indicator>
      </CheckboxPrimitive.Root>
    );
  }
);

CheckboxV2.displayName = 'CheckboxV2';
